import { useEffect, useState } from "react";
import { Flame, ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { fmtOdds } from "@/lib/odds";

const lastSeen = new Map();

// flashes red when a price just shortened (money on this side) and green when it drifted out (better number now)
export function useLineFlash(key, current) {
  const [flash, setFlash] = useState("");
  useEffect(() => {
    if (!key || current == null) return;
    const prev = lastSeen.get(key);
    lastSeen.set(key, current);
    if (prev == null || prev === current) return;
    setFlash(imp(current) > imp(prev) ? "flash-in" : "flash-out");
    const t = setTimeout(() => setFlash(""), 2600);
    return () => clearTimeout(t);
  }, [key, current]);
  return flash;
}

const imp = (o) => 1 / (1 + (o > 0 ? o / 100 : 100 / Math.abs(o)));

export function Sparkline({ history = [], width = 56, height = 18, color = "#94A3B8" }) {
  if (history.length < 2) return null;
  const ps = history.map(imp);
  const min = Math.min(...ps), max = Math.max(...ps);
  const span = max - min || 0.01;
  const pts = ps.map((p, i) => `${(i / (ps.length - 1)) * width},${height - 2 - ((p - min) / span) * (height - 4)}`).join(" ");
  return (
    <svg width={width} height={height} className="shrink-0" data-testid="move-sparkline">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export function MoveChip({ movement, compact = false, spark = true }) {
  const flash = useLineFlash(movement?.key || movement?.label, movement?.current);
  if (!movement) return null;
  const { open, current, delta_pts, direction, steam, history } = movement;
  const style = direction === "in" ? "border-red-500/40 bg-red-500/10 text-red-300" : direction === "out" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-[#26334D] bg-[#0B0E14] text-slate-500";
  const color = direction === "in" ? "#F87171" : direction === "out" ? "#34D399" : "#64748B";
  const Icon = direction === "in" ? ArrowDownRight : direction === "out" ? ArrowUpRight : Minus;
  if (compact)
    return (
      <span data-testid="move-chip" data-flash={flash || undefined} title={`Opened ${fmtOdds(open)} · now ${fmtOdds(current)} (${delta_pts > 0 ? "+" : ""}${delta_pts} pts)`} className={`inline-flex items-center gap-0.5 rounded px-1 py-0.5 font-mono-num text-[10px] font-bold ${style} ${flash}`}>
        {steam && <Flame size={9} className="text-orange-400" />}<Icon size={10} />{direction === "flat" ? "flat" : fmtOdds(current)}
      </span>
    );
  return (
    <span data-testid="move-chip" data-flash={flash || undefined} className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono-num text-[11px] font-semibold ${style} ${flash}`}>
      {steam && <span className="flex items-center gap-0.5 text-orange-400" data-testid="steam-flag"><Flame size={11} /> Steam</span>}
      <span className="text-slate-500">{fmtOdds(open)}</span>
      <span className="text-slate-600">→</span>
      <span>{fmtOdds(current)}</span>
      <Icon size={12} />
      {direction !== "flat" && <span>{delta_pts > 0 ? "+" : ""}{delta_pts}</span>}
      {spark && <Sparkline history={history} color={color} />}
    </span>
  );
}
